import React, { useState, useMemo } from 'react';
import { shallowEqualIgnoringFunctions } from '../utils/memoCompare';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  Bar, ComposedChart, Legend, Cell, ReferenceLine,
} from 'recharts';
import { toastSuccess, toastError } from '../ui/toast';
import { generateCoachBrief, getEffectiveBriefingWeeks } from '../utils/performanceBriefing';
import { structureSwaps } from '../services/swapService';
import WorkoutHeatmap from './charts/WorkoutHeatmap';
import SessionResponseChart from './charts/SessionResponseChart';
import StrengthProgress from './stats/progress/StrengthProgress';
import AllExercisesDrilldown from './stats/progress/AllExercisesDrilldown';
import { brandColors as bc } from '../styles/clientManagementStyles';
import { MOVEMENT_CATEGORY_COLORS } from '../constants/exerciseCategories';
import { colors as theme } from '../theme';

// `styles.card` from clientManagementStyles.js does not actually exist —
// every card on this page that spread it in was rendering with no
// background/border/radius of its own. Defining a real one here instead of
// propagating that into the redesign.
const card = {
  backgroundColor: bc.mediumGray,
  border: `1px solid ${bc.lightGray}`,
  borderRadius: '14px',
};

const SUB_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'strength', label: 'Strength' },
  { id: 'trainingLoad', label: 'Training Load' },
  { id: 'consistency', label: 'Consistency' },
];

const fmtK = (n) => `${(n / 1000).toFixed(1)}k`;

// ── Coach Brief ─────────────────────────────────────────────────────────
const CoachBrief = ({
  selectedClient, performanceAnalytics, timeRange, clientCoachNotes,
  briefingText, setBriefingText, sendingNote, sendCoachNote, perfDrop, swaps, food,
}) => {
  const sessionFreq = performanceAnalytics.sessionFrequency || [];
  const totalSessions = sessionFreq.reduce((s, w) => s + w.sessions, 0);
  const avgFrequency = sessionFreq.length > 0 ? totalSessions / sessionFreq.length : 0;
  const perfColor = perfDrop >= 0 ? bc.green : bc.red;

  const kpis = [
    { label: 'Sessions', value: totalSessions, color: bc.white },
    { label: 'Performance', value: `${perfDrop >= 0 ? '+' : ''}${performanceAnalytics.performanceDrop}%`, color: perfColor },
    { label: 'Frequency', value: `${avgFrequency.toFixed(1)}/wk`, color: bc.white },
    { label: 'Volume', value: fmtK(performanceAnalytics.totalVolume), color: bc.gold },
  ];

  return (
    <div style={{ ...card, marginBottom: '16px', padding: '18px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
        <div>
          <h2 style={{ color: bc.white, margin: 0, fontSize: '16px', fontWeight: '700' }}>Coach Brief</h2>
          <p style={{ color: bc.silver, margin: '3px 0 0', fontSize: '12px' }}>
            {selectedClient?.full_name} · Auto-generated — edit before sharing
          </p>
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button
            onClick={() => {
              const effectiveWeeks = getEffectiveBriefingWeeks(selectedClient?.created_at, timeRange);
              setBriefingText(generateCoachBrief(performanceAnalytics, { timeRangeWeeks: effectiveWeeks, food, swaps }));
            }}
            style={{ padding: '6px 12px', borderRadius: '8px', border: `1px solid ${bc.lightGray}`, backgroundColor: 'transparent', color: bc.silver, fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
          >
            Regenerate
          </button>
          <button
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(briefingText);
                toastSuccess('Briefing copied to clipboard');
              } catch {
                toastError('Could not copy — please select and copy manually');
              }
            }}
            disabled={!briefingText}
            style={{ padding: '6px 12px', borderRadius: '8px', border: `1px solid ${bc.lightGray}`, backgroundColor: 'transparent', color: bc.silver, fontSize: '12px', fontWeight: '600', cursor: briefingText ? 'pointer' : 'not-allowed' }}
          >
            Copy
          </button>
          <button
            onClick={() => sendCoachNote(briefingText)}
            disabled={!briefingText || sendingNote}
            style={{ padding: '6px 12px', borderRadius: '8px', border: 'none', backgroundColor: briefingText && !sendingNote ? bc.gold : bc.lightGray, color: briefingText && !sendingNote ? bc.darkGray : bc.silver, fontSize: '12px', fontWeight: '700', cursor: briefingText && !sendingNote ? 'pointer' : 'not-allowed' }}
          >
            {sendingNote ? 'Sending...' : 'Send to Client'}
          </button>
        </div>
      </div>

      <textarea
        value={briefingText}
        onChange={(e) => setBriefingText(e.target.value)}
        rows={5}
        style={{
          width: '100%', padding: '10px 12px', backgroundColor: bc.darkGray,
          border: `1px solid ${bc.lightGray}`, borderRadius: '8px',
          color: bc.white, fontSize: '13px', lineHeight: 1.5,
          fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box',
          marginBottom: '12px',
        }}
      />

      <p style={{ color: bc.gold, margin: '0 0 12px', fontSize: '11px' }}>
        {clientCoachNotes.length > 0
          ? `📨 Last sent ${new Date(clientCoachNotes[0].created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} (${clientCoachNotes.length} total)`
          : 'No notes sent yet'}
      </p>

      {/* Compact KPI snapshot */}
      <div style={{ display: 'flex', alignItems: 'stretch', borderTop: `1px solid ${bc.lightGray}`, paddingTop: '12px' }}>
        {kpis.map((k, i) => (
          <React.Fragment key={k.label}>
            {i > 0 && <div style={{ width: '1px', backgroundColor: bc.lightGray, margin: '0 16px' }} />}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: k.color, fontSize: '20px', fontWeight: '800', lineHeight: 1.1 }}>{k.value}</div>
              <div style={{ color: bc.silver, fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px', marginTop: '2px' }}>{k.label}</div>
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

// ── Attention Required ──────────────────────────────────────────────────
const buildAttentionItems = (performanceAnalytics, sessionFlagSummary) => {
  if (!performanceAnalytics) return [];
  const items = [];

  if (sessionFlagSummary?.flaggedCount > 0) {
    const { flaggedCount, totalSessions } = sessionFlagSummary;
    items.push({
      severity: 'red',
      icon: '🔥',
      title: `${flaggedCount} High-Demand Session${flaggedCount !== 1 ? 's' : ''}`,
      detail: `Low readiness combined with high effort in ${flaggedCount} of the last ${totalSessions} sessions`,
      tab: 'overview',
    });
  }

  if (performanceAnalytics.alert) {
    items.push({
      severity: performanceAnalytics.alert.severity === 'danger' ? 'red' : 'orange',
      icon: '⚠',
      title: performanceAnalytics.alert.type === 'overtraining' ? 'Possible Overtraining' : 'Performance Drop',
      detail: performanceAnalytics.alert.message,
      tab: 'trainingLoad',
    });
  }

  if (performanceAnalytics.needsAttention?.length > 0) {
    const names = performanceAnalytics.needsAttention.slice(0, 2).map(l => l.exerciseName).join(', ');
    items.push({
      severity: 'orange',
      icon: '📉',
      title: `${performanceAnalytics.needsAttention.length} Lift${performanceAnalytics.needsAttention.length !== 1 ? 's' : ''} Plateaued`,
      detail: `${names}${performanceAnalytics.needsAttention.length > 2 ? ' and more' : ''} trending down — consider a deload or variation`,
      tab: 'strength',
    });
  }

  if (performanceAnalytics.abandonedSessions?.length > 0) {
    items.push({
      severity: 'orange',
      icon: '⏸',
      title: `${performanceAnalytics.abandonedSessions.length} Abandoned Session${performanceAnalytics.abandonedSessions.length !== 1 ? 's' : ''}`,
      detail: 'Started but never marked complete or missed',
      tab: 'consistency',
    });
  }

  if (performanceAnalytics.skippedExercises?.length > 0) {
    const top = performanceAnalytics.skippedExercises[0];
    items.push({
      severity: 'orange',
      icon: '🚫',
      title: `${performanceAnalytics.skippedExercises.length} Frequently Skipped Exercise${performanceAnalytics.skippedExercises.length !== 1 ? 's' : ''}`,
      detail: `"${top.name}" skipped ${top.count}× — may need replacing`,
      tab: 'strength',
    });
  }

  const order = { red: 0, orange: 1 };
  return items.sort((a, b) => order[a.severity] - order[b.severity]).slice(0, 5);
};

const AttentionRequired = ({ items, onViewDetails }) => {
  if (items.length === 0) {
    return (
      <div style={{ ...card, marginBottom: '16px', padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '10px', border: `1px solid rgba(52,199,89,0.25)`, backgroundColor: 'rgba(52,199,89,0.06)' }}>
        <span style={{ fontSize: '16px' }}>✓</span>
        <span style={{ color: bc.white, fontSize: '13px', fontWeight: '600' }}>Nothing needs attention right now</span>
      </div>
    );
  }

  return (
    <div style={{ ...card, marginBottom: '16px', padding: '16px 18px' }}>
      <h2 style={{ color: bc.white, margin: '0 0 10px', fontSize: '15px', fontWeight: '700' }}>Attention Required</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {items.map((item, i) => {
          const color = item.severity === 'red' ? bc.red : bc.warning;
          return (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              padding: '10px 12px', borderRadius: '8px',
              backgroundColor: `${color}12`, border: `1px solid ${color}33`, borderLeft: `3px solid ${color}`,
            }}>
              <span style={{ fontSize: '15px', flexShrink: 0 }}>{item.icon}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color, fontSize: '12px', fontWeight: '700' }}>{item.title}</div>
                <div style={{ color: bc.silver, fontSize: '11px', marginTop: '1px' }}>{item.detail}</div>
              </div>
              <button
                onClick={() => onViewDetails(item.tab)}
                style={{ background: 'none', border: 'none', color, fontSize: '11px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                View details ›
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Sub-tab nav ──────────────────────────────────────────────────────────
const SubTabNav = ({ active, onChange }) => (
  <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', flexWrap: 'wrap' }}>
    {SUB_TABS.map(t => (
      <button
        key={t.id}
        onClick={() => onChange(t.id)}
        style={{
          padding: '8px 16px',
          borderRadius: '8px',
          border: `1px solid ${active === t.id ? bc.gold : bc.lightGray}`,
          backgroundColor: active === t.id ? 'rgba(212,175,55,0.12)' : 'transparent',
          color: active === t.id ? bc.gold : bc.silver,
          fontSize: '13px',
          fontWeight: active === t.id ? '700' : '500',
          cursor: 'pointer',
          transition: 'all 0.15s',
        }}
      >
        {t.label}
      </button>
    ))}
  </div>
);

// ── Small section header used throughout the sub-tab panes ─────────────
const PaneHeader = ({ title, subtitle, accent = bc.white, right }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '14px', gap: '8px', flexWrap: 'wrap' }}>
    <div>
      <h3 style={{ color: accent, margin: 0, fontSize: '15px', fontWeight: '700' }}>{title}</h3>
      {subtitle && <p style={{ color: bc.silver, margin: '3px 0 0', fontSize: '12px' }}>{subtitle}</p>}
    </div>
    {right}
  </div>
);

// ── Overview pane ────────────────────────────────────────────────────────
const OverviewPane = ({ selectedClient, performanceAnalytics, onSessionSummary, onViewDetails }) => (
  // Side by side on desktop, same reasoning as Coach Brief's KPI row —
  // Session Response's SVG holds a fixed aspect ratio, so left unconstrained
  // it stretches to fill the column and gets disproportionately tall.
  <div className="cm-split" style={{ display: 'grid', gridTemplateColumns: 'minmax(380px, 520px) 1fr', gap: '16px', alignItems: 'start' }}>
    <SessionResponseChart
      userId={selectedClient?.auth_user_id || selectedClient?.user_id}
      showInsight
      onSummary={onSessionSummary}
    />

    {/* Recent PR / progress summary */}
    <div style={{ ...card, padding: '18px' }}>
      <PaneHeader
        title="Recent Progress"
        subtitle={
          // No record yet (or no number to go on): say so, rather than guess at a plateau
          performanceAnalytics.daysSinceLastPR == null || Number.isNaN(performanceAnalytics.daysSinceLastPR)
            ? 'Not enough training history yet'
            : performanceAnalytics.daysSinceLastPR === 0
            ? 'PR set today — great work!'
            : performanceAnalytics.daysSinceLastPR === 1
            ? 'PR set yesterday — great work!'
            : performanceAnalytics.daysSinceLastPR <= 7
            ? `PR set ${performanceAnalytics.daysSinceLastPR} days ago — keep pushing!`
            : performanceAnalytics.daysSinceLastPR <= 21
            ? `Last PR ${performanceAnalytics.daysSinceLastPR} days ago — stay consistent`
            : `No new PRs for ${performanceAnalytics.daysSinceLastPR} days — may signal a plateau`
        }
        right={
          performanceAnalytics.prBoard.length > 0 && (
            <button onClick={() => onViewDetails('strength')} style={{ background: 'none', border: 'none', color: bc.gold, fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}>
              View all {performanceAnalytics.prBoard.length} ›
            </button>
          )
        }
      />
      {performanceAnalytics.prBoard.length === 0 ? (
        <div style={{ color: bc.silver, fontSize: '13px' }}>No personal records yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {performanceAnalytics.prBoard.slice(0, 3).map((pr, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '8px 10px', borderRadius: '8px', backgroundColor: i % 2 === 0 ? 'rgba(255,255,255,0.03)' : 'transparent' }}>
              <span style={{ color: bc.silver, fontSize: '12px', width: '20px', flexShrink: 0 }}>#{i + 1}</span>
              <span style={{ color: bc.white, fontSize: '13px', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pr.name}</span>
              <span style={{ color: bc.gold, fontSize: '13px', fontWeight: '700', marginRight: '12px' }}>{pr.maxWeight}kg</span>
              <span style={{ color: bc.silver, fontSize: '11px', whiteSpace: 'nowrap' }}>
                {pr.reps} reps · {pr.maxWeightDate ? new Date(pr.maxWeightDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);

// ── Strength pane ────────────────────────────────────────────────────────
// Reuses the client Stats > Progress "Strength Progress" system (tiered
// Top Improvements / Needs Attention / New Baselines, tier-coloured bars,
// category colour only as a thin accent strip) instead of the coach page's
// old separate StrengthDashboard, which coloured bars by muscle category —
// the same clash already fixed on the client side (e.g. a green "Chest"
// bar sitting next to a green "improvement" badge reads as two different
// things needing one colour). `showRange` adds the lowest/highest weight
// actually logged plus the most recent session date per exercise — new
// info, opt-in via a prop so the client's own view is unaffected.
const StrengthPane = ({ selectedClient, performanceAnalytics }) => {
  const [viewAllExercises, setViewAllExercises] = useState(null);
  return (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
    <StrengthProgress
      userId={selectedClient?.user_id}
      onViewAll={setViewAllExercises}
      showRange
      preloadedSummary={performanceAnalytics?.strengthSummary}
    />
    {viewAllExercises && (
      <AllExercisesDrilldown exercises={viewAllExercises} onClose={() => setViewAllExercises(null)} />
    )}

    {performanceAnalytics.prBoard.length > 0 && (
      <div style={{ ...card, padding: '20px' }}>
        <PaneHeader
          title="Personal Records"
          right={
            <div style={{ background: 'rgba(212,175,55,0.15)', border: `1px solid rgba(212,175,55,0.3)`, borderRadius: '20px', padding: '3px 10px', color: bc.gold, fontSize: '12px', fontWeight: '600' }}>
              {performanceAnalytics.prBoard.length} exercises
            </div>
          }
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {performanceAnalytics.prBoard.map((pr, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '8px 10px', borderRadius: '8px', backgroundColor: i % 2 === 0 ? 'rgba(255,255,255,0.03)' : 'transparent' }}>
              <span style={{ color: bc.silver, fontSize: '12px', width: '22px', flexShrink: 0 }}>#{i + 1}</span>
              <span style={{ color: bc.white, fontSize: '13px', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pr.name}</span>
              <span style={{ color: bc.gold, fontSize: '13px', fontWeight: '700', marginRight: '14px' }}>{pr.maxWeight}kg</span>
              <span style={{ color: bc.silver, fontSize: '11px', whiteSpace: 'nowrap' }}>
                {pr.reps} reps · {pr.maxWeightDate ? new Date(pr.maxWeightDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
              </span>
            </div>
          ))}
        </div>
      </div>
    )}

    {performanceAnalytics.needsAttention?.length > 0 && (
      <div style={{ ...card, padding: '18px', backgroundColor: 'rgba(255,149,0,0.06)', border: `1px solid rgba(255,149,0,0.3)` }}>
        <PaneHeader title="Plateau Alert" subtitle="Trending down over the last 3 months — consider deload, rep range change, or exercise variation" accent={bc.warning} />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {performanceAnalytics.needsAttention.map((lift, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 12px', backgroundColor: 'rgba(255,149,0,0.12)', borderRadius: '8px', border: '1px solid rgba(255,149,0,0.25)' }}>
              <span style={{ color: bc.white, fontSize: '13px', fontWeight: '600' }}>{lift.exerciseName}</span>
              <span style={{ color: bc.warning, fontSize: '12px' }}>↓ {Math.abs(lift.increasePercent)}% ({lift.newestRM}kg)</span>
            </div>
          ))}
        </div>
      </div>
    )}

    {performanceAnalytics.skippedExercises?.length > 0 && (
      <div style={{ ...card, padding: '18px' }}>
        <PaneHeader title="Most Skipped Exercises" subtitle="Remove or replace — if they're skipping it, they're not benefiting from it" />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {performanceAnalytics.skippedExercises.map((ex, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '8px 10px', borderRadius: '8px', backgroundColor: i % 2 === 0 ? 'rgba(255,255,255,0.03)' : 'transparent' }}>
              <span style={{ color: bc.silver, fontSize: '12px', width: '20px', flexShrink: 0 }}>#{i + 1}</span>
              <span style={{ color: bc.white, fontSize: '13px', flex: 1 }}>{ex.name}</span>
              <span style={{ padding: '3px 9px', borderRadius: '8px', backgroundColor: 'rgba(255,69,58,0.15)', color: bc.red, fontSize: '11px', fontWeight: '600' }}>{ex.count}× skipped</span>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
  );
};

// ── Training Load pane ───────────────────────────────────────────────────
const TrainingLoadPane = ({ performanceAnalytics, activePieIndex, setActivePieIndex, MUSCLE_COLORS }) => {
  const bwPoints = (performanceAnalytics.weeklyData || []).filter(w => w.bodyweight !== null);
  const cs = performanceAnalytics.cardioStats;
  const mgData = performanceAnalytics.muscleGroupVolume || [];
  const totalVol = mgData.reduce((s, e) => s + e.value, 0);
  const INTENSITY_COLORS = { low: theme.success, moderate: '#FFCC00', high: theme.warning, maximum: theme.danger };
  const INTENSITY_LABELS = { low: 'Low', moderate: 'Mod', high: 'High', maximum: 'Max' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {performanceAnalytics.weeklyData.length > 0 && (
        <div style={{ ...card, padding: '20px' }}>
          <PaneHeader title="Weekly Volume & Performance Trend" subtitle="Rising volume with falling avg weight is an early overtraining signal" />
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={performanceAnalytics.weeklyData}>
              <CartesianGrid strokeDasharray="3 3" stroke={bc.lightGray} />
              <XAxis dataKey="week" stroke={bc.silver} tick={{ fill: bc.silver, fontSize: 12 }}
                tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} />
              <YAxis yAxisId="left" stroke={bc.gold} tick={{ fill: bc.silver, fontSize: 12 }}
                label={{ value: 'Volume (kg)', angle: -90, position: 'insideLeft', fill: bc.gold }} />
              <YAxis yAxisId="right" orientation="right" stroke={bc.green} tick={{ fill: bc.silver, fontSize: 12 }}
                label={{ value: 'Avg Weight (kg)', angle: 90, position: 'insideRight', fill: bc.green }} />
              <Tooltip
                contentStyle={{ backgroundColor: bc.mediumGray, border: `1px solid ${bc.lightGray}`, borderRadius: '8px' }}
                labelStyle={{ color: bc.white }} itemStyle={{ color: bc.white }}
                formatter={(value, name) => {
                  if (name === 'volumeKg') return [value + ' kg', 'Volume'];
                  if (name === 'avgWeight') return [value + ' kg', 'Avg Weight'];
                  return [value, name];
                }}
                labelFormatter={(label) => `Week of ${new Date(label).toLocaleDateString()}`}
              />
              <Legend wrapperStyle={{ color: bc.white }} formatter={(value) => {
                if (value === 'volumeKg') return <span style={{ color: bc.gold }}>Weekly Volume</span>;
                if (value === 'avgWeight') return <span style={{ color: bc.green }}>Avg Lifted Weight</span>;
                return value;
              }} />
              <Bar yAxisId="left" dataKey="volumeKg" fill={bc.gold} radius={[8, 8, 0, 0]} opacity={0.8} />
              <Line yAxisId="right" type="monotone" dataKey="avgWeight" stroke={bc.green} strokeWidth={3} dot={{ fill: bc.green, r: 5 }} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="cm-split" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        {/* Muscle Group Split — bars only, donut removed per redesign brief */}
        {mgData.length > 0 && (
          <div style={{ ...card, padding: '20px' }}>
            <PaneHeader title="Muscle Group Split" subtitle="Imbalances here often predict injury" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {mgData.map((entry, index) => {
                const pct = totalVol > 0 ? (entry.value / totalVol) * 100 : 0;
                const color = MUSCLE_COLORS[entry.name] || bc.silver;
                const dimmed = activePieIndex !== null && activePieIndex !== index;
                return (
                  <div
                    key={entry.name}
                    onMouseEnter={() => setActivePieIndex(index)}
                    onMouseLeave={() => setActivePieIndex(null)}
                    style={{ opacity: dimmed ? 0.4 : 1, transition: 'opacity 0.15s' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
                        <span style={{ color: bc.white, fontSize: '12px' }}>{entry.name}</span>
                      </div>
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        {entry.sets > 0 && <span style={{ color: bc.gold, fontSize: '11px', fontWeight: '600' }}>{entry.sets} sets</span>}
                        <span style={{ color: bc.silver, fontSize: '12px' }}>{pct.toFixed(0)}%</span>
                      </div>
                    </div>
                    <div style={{ background: bc.lightGray, borderRadius: '4px', height: '4px' }}>
                      <div style={{ width: `${pct}%`, height: '100%', borderRadius: '4px', backgroundColor: color, opacity: 0.85 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {bwPoints.length > 0 && (
          <div style={{ ...card, padding: '20px' }}>
            <PaneHeader title="Bodyweight Trend" subtitle="Gaining weight while volume rises usually means muscle" />
            <ResponsiveContainer width="100%" height={170}>
              <LineChart data={bwPoints}>
                <CartesianGrid strokeDasharray="3 3" stroke={bc.lightGray} vertical={false} />
                <XAxis dataKey="week" axisLine={false} tickLine={false} tick={{ fill: bc.silver, fontSize: 12 }}
                  tickFormatter={(v) => { const d = new Date(v); return `${d.getMonth() + 1}/${d.getDate()}`; }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: bc.silver, fontSize: 12 }} domain={['auto', 'auto']} width={36} />
                <Tooltip
                  contentStyle={{ backgroundColor: bc.mediumGray, border: `1px solid ${bc.lightGray}`, borderRadius: '8px' }}
                  labelStyle={{ color: bc.white }} itemStyle={{ color: '#64D2FF' }}
                  formatter={(value) => [value + ' kg', 'Bodyweight']}
                  labelFormatter={(label) => `Week of ${new Date(label).toLocaleDateString()}`}
                />
                <ReferenceLine y={bwPoints[0].bodyweight} stroke={bc.lightGray} strokeDasharray="4 4"
                  label={{ value: 'Start', position: 'insideTopLeft', fill: bc.silver, fontSize: 11 }} />
                <Line type="monotone" dataKey="bodyweight" stroke="#64D2FF" strokeWidth={3} dot={{ fill: '#64D2FF', r: 5, strokeWidth: 0 }} activeDot={{ r: 7 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {cs && cs.totalSessions > 0 && (
        <div style={{ ...card, padding: '20px' }}>
          <PaneHeader title="Cardio Performance" subtitle="Cardio sessions logged in the selected period" />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '16px' }}>
            {[{ label: 'Total Sessions', value: cs.totalSessions }, { label: 'Total Minutes', value: `${cs.totalCardioMins}m` }].map((c, i) => (
              <div key={i} style={{ backgroundColor: 'rgba(0,122,255,0.08)', border: '1px solid rgba(0,122,255,0.2)', borderTop: '3px solid #007AFF', borderRadius: '8px', padding: '12px' }}>
                <div style={{ color: bc.silver, fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{c.label}</div>
                <div style={{ color: bc.white, fontSize: '24px', fontWeight: '800', lineHeight: 1.1, marginTop: '4px' }}>{c.value}</div>
              </div>
            ))}
          </div>
          {cs.cardioBreakdown.map((ex, i) => (
            <div key={i} style={{ backgroundColor: 'rgba(0,0,0,0.2)', border: `1px solid ${theme.surface3}`, borderRadius: '8px', padding: '14px', marginBottom: i < cs.cardioBreakdown.length - 1 ? '10px' : 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <span style={{ color: bc.white, fontSize: '13px', fontWeight: '700' }}>{ex.name}</span>
                <span style={{ color: bc.silver, fontSize: '11px' }}>{ex.totalSessions} session{ex.totalSessions !== 1 ? 's' : ''}</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {ex.avgDurationMins > 0 && <span style={{ padding: '3px 9px', borderRadius: '8px', backgroundColor: 'rgba(0,122,255,0.12)', color: '#007AFF', fontSize: '11px', fontWeight: '600' }}>avg {ex.avgDurationMins}m</span>}
                {ex.dominantIntensity && <span style={{ padding: '3px 9px', borderRadius: '8px', backgroundColor: `${INTENSITY_COLORS[ex.dominantIntensity]}20`, color: INTENSITY_COLORS[ex.dominantIntensity], fontSize: '11px', fontWeight: '600' }}>mainly {INTENSITY_LABELS[ex.dominantIntensity]}</span>}
                {ex.totalDistanceKm > 0 && <span style={{ padding: '3px 9px', borderRadius: '8px', backgroundColor: 'rgba(175,82,222,0.12)', color: '#AF52DE', fontSize: '11px', fontWeight: '600' }}>{ex.totalDistanceKm} km total</span>}
                {ex.totalCalories > 0 && <span style={{ padding: '3px 9px', borderRadius: '8px', backgroundColor: 'rgba(255,159,10,0.12)', color: '#FF9F0A', fontSize: '11px', fontWeight: '600' }}>{ex.totalCalories} cal total</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Consistency pane ─────────────────────────────────────────────────────
const ConsistencyPane = ({
  selectedClient, performanceAnalytics, clientMetrics,
  sessionNoteEditing, setSessionNoteEditing, sessionNoteText, setSessionNoteText,
  sessionNoteSaving, saveSessionNote,
}) => {
  const [abandonedExpanded, setAbandonedExpanded] = useState(false);
  const freqData = performanceAnalytics.sessionFrequency || [];
  const avgSessions = freqData.length > 0 ? freqData.reduce((s, w) => s + w.sessions, 0) / freqData.length : 0;
  const getBarColor = (val) => val >= Math.ceil(avgSessions) ? bc.green : val >= Math.floor(avgSessions) ? bc.gold : bc.warning;
  const FreqLabel = ({ x, y, width, value }) =>
    value > 0 ? <text x={x + width / 2} y={y - 8} fill={bc.white} textAnchor="middle" fontSize={13} fontWeight="700">{value}</text> : null;

  const recentSessions = clientMetrics[selectedClient?.user_id]?.recentSessions || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div className="cm-split" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        {freqData.length > 0 && (
          <div style={{ ...card, padding: '20px' }}>
            <PaneHeader
              title="Training Frequency"
              subtitle="Spot missed weeks and consistency drops"
              right={
                <div style={{ display: 'flex', gap: '10px', fontSize: '11px' }}>
                  <span style={{ color: bc.green }}>● Above</span>
                  <span style={{ color: bc.gold }}>● Avg</span>
                  <span style={{ color: bc.warning }}>● Below</span>
                </div>
              }
            />
            <ResponsiveContainer width="100%" height={190}>
              <ComposedChart data={freqData} margin={{ top: 20, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={bc.lightGray} vertical={false} />
                <XAxis dataKey="week" axisLine={false} tickLine={false} tick={{ fill: bc.silver, fontSize: 12 }}
                  tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: bc.silver, fontSize: 12 }} allowDecimals={false} width={26} />
                <Tooltip
                  cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                  contentStyle={{ backgroundColor: bc.mediumGray, border: `1px solid ${bc.lightGray}`, borderRadius: '8px' }}
                  labelStyle={{ color: bc.white }} itemStyle={{ color: bc.white }}
                  formatter={(value) => [value, 'Sessions']}
                  labelFormatter={(label) => `Week of ${new Date(label).toLocaleDateString()}`}
                />
                <ReferenceLine y={parseFloat(avgSessions.toFixed(1))} stroke={bc.silver} strokeDasharray="6 4"
                  label={{ value: `Avg ${avgSessions.toFixed(1)}`, position: 'insideTopRight', fill: bc.silver, fontSize: 11 }} />
                <Bar dataKey="sessions" radius={[8, 8, 0, 0]} label={<FreqLabel />}>
                  {freqData.map((entry, index) => <Cell key={index} fill={getBarColor(entry.sessions)} opacity={0.9} />)}
                </Bar>
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}

        <WorkoutHeatmap userId={selectedClient?.user_id} />
      </div>

      {performanceAnalytics.abandonedSessions?.length > 0 && (
        <div style={{ ...card, padding: '18px', borderLeft: `3px solid ${bc.warning}` }}>
          <PaneHeader
            title={`Abandoned Sessions (${performanceAnalytics.abandonedSessions.length})`}
            subtitle={abandonedExpanded ? 'Started but never marked complete or missed' : undefined}
            right={
              <button
                onClick={() => setAbandonedExpanded(v => !v)}
                style={{ background: 'none', border: 'none', color: bc.warning, fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}
              >
                {abandonedExpanded ? 'Hide ▲' : 'Show ▼'}
              </button>
            }
          />
          {abandonedExpanded && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {performanceAnalytics.abandonedSessions.map(s => (
                <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: bc.silver }}>
                  <span>{s.label}</span>
                  <span>{s.daysAgo} days ago</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {recentSessions.length > 0 && (
        <div style={{ ...card, padding: '18px' }}>
          <PaneHeader title="Recent Sessions" subtitle="Leave feedback the client will see next time they open the app" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {recentSessions.map(session => {
              const isEditing = sessionNoteEditing === session.id;
              const dateStr = new Date(session.completed_at || session.started_at)
                .toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
              return (
                <div key={session.id} style={{
                  backgroundColor: 'rgba(255,255,255,0.03)',
                  border: `1px solid ${isEditing ? bc.gold : bc.lightGray}`,
                  borderRadius: '8px', padding: '12px 14px', transition: 'border-color 0.15s',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: bc.white, fontSize: '13px', fontWeight: 600 }}>{dateStr}</div>
                      {session.coach_notes && !isEditing && (
                        <div style={{ color: bc.green, fontSize: '12px', marginTop: '3px', fontStyle: 'italic' }}>"{session.coach_notes}"</div>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        if (isEditing) { setSessionNoteEditing(null); setSessionNoteText(''); }
                        else { setSessionNoteEditing(session.id); setSessionNoteText(session.coach_notes || ''); }
                      }}
                      style={{
                        backgroundColor: isEditing ? 'transparent' : 'rgba(212,175,55,0.1)',
                        border: `1px solid ${isEditing ? bc.lightGray : bc.gold}`, borderRadius: '8px',
                        color: isEditing ? bc.silver : bc.gold, fontSize: '12px', fontWeight: 600,
                        padding: '5px 10px', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                      }}
                    >
                      {isEditing ? 'Cancel' : session.coach_notes ? 'Edit' : '+ Feedback'}
                    </button>
                  </div>
                  {isEditing && (
                    <div style={{ marginTop: '10px' }}>
                      <textarea
                        autoFocus rows={2}
                        placeholder="e.g. Great effort today — push the weight up next session"
                        value={sessionNoteText}
                        onChange={e => setSessionNoteText(e.target.value)}
                        style={{
                          width: '100%', backgroundColor: bc.darkGray, border: `1px solid ${bc.lightGray}`,
                          borderRadius: '8px', color: bc.white, fontSize: '13px', padding: '9px 12px',
                          marginBottom: '8px', boxSizing: 'border-box', resize: 'none', outline: 'none', fontFamily: 'inherit',
                        }}
                      />
                      <button
                        onClick={() => saveSessionNote(session.id)}
                        disabled={sessionNoteSaving}
                        style={{
                          backgroundColor: bc.green, border: 'none', borderRadius: '8px', color: theme.text,
                          fontSize: '13px', fontWeight: 700, padding: '8px 16px',
                          cursor: sessionNoteSaving ? 'not-allowed' : 'pointer', opacity: sessionNoteSaving ? 0.6 : 1,
                        }}
                      >
                        {sessionNoteSaving ? 'Saving...' : 'Save Feedback'}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main component ───────────────────────────────────────────────────────
const ClientPerformanceTab = ({
  selectedClient,
  performanceAnalytics,
  clientSwaps,
  clientFood,
  loadingAnalytics,
  timeRange,
  setTimeRange,
  dateSpan,
  clientCoachNotes,
  briefingText,
  setBriefingText,
  sendingNote,
  sendCoachNote,
  activePieIndex,
  setActivePieIndex,
  clientMetrics,
  sessionNoteEditing,
  setSessionNoteEditing,
  sessionNoteText,
  setSessionNoteText,
  sessionNoteSaving,
  saveSessionNote,
}) => {
  const MUSCLE_COLORS = MOVEMENT_CATEGORY_COLORS;
  const perfDrop = parseFloat(performanceAnalytics?.performanceDrop || 0);
  const [activeSubTab, setActiveSubTab] = useState('overview');
  const [sessionFlagSummary, setSessionFlagSummary] = useState(null);
  // The swaps worth a word in the brief (pain, or the same exercise swapped again and again)
  const swapsToActOn = useMemo(() => (Array.isArray(clientSwaps) ? structureSwaps(clientSwaps).actOn : []), [clientSwaps]);

  const attentionItems = useMemo(
    () => buildAttentionItems(performanceAnalytics, sessionFlagSummary),
    [performanceAnalytics, sessionFlagSummary]
  );

  return (
    <div style={{ marginBottom: '24px' }}>
      {/* Header row — title + time filter */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
        <div>
          <h2 style={{ color: bc.gold, margin: 0, fontSize: '18px', fontWeight: '600' }}>Performance Analytics</h2>
          <p style={{ color: bc.silver, margin: '4px 0 0', fontSize: '13px' }}>{selectedClient?.full_name}</p>
          <p style={{ color: bc.silver, margin: '4px 0 0', fontSize: '13px' }}>{dateSpan}</p>
        </div>
        <div style={{ display: 'flex', gap: '6px', background: bc.mediumGray, padding: '4px', borderRadius: '20px', border: `1px solid ${bc.lightGray}` }}>
          {[1, 4, 8, 12, 'all'].map(w => (
            <button
              key={w}
              onClick={() => setTimeRange(w)}
              style={{
                padding: '6px 14px', borderRadius: '20px', border: 'none',
                backgroundColor: timeRange === w ? bc.gold : 'transparent',
                color: timeRange === w ? bc.darkGray : bc.silver,
                fontWeight: timeRange === w ? '700' : '500', cursor: 'pointer', fontSize: '13px', transition: 'all 0.15s',
              }}
            >
              {w === 'all' ? 'All' : w === 1 ? '7D' : `${w}W`}
            </button>
          ))}
        </div>
      </div>

      {loadingAnalytics ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ width: '40px', height: '40px', border: `3px solid ${bc.mediumGray}`, borderTop: `3px solid ${bc.gold}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
          <p style={{ color: bc.silver }}>Loading analytics...</p>
        </div>
      ) : performanceAnalytics ? (
        <>
          <CoachBrief
            selectedClient={selectedClient}
            performanceAnalytics={performanceAnalytics}
            timeRange={timeRange}
            clientCoachNotes={clientCoachNotes}
            briefingText={briefingText}
            setBriefingText={setBriefingText}
            sendingNote={sendingNote}
            sendCoachNote={sendCoachNote}
            perfDrop={perfDrop}
            swaps={swapsToActOn}
            food={clientFood}
          />

          <AttentionRequired items={attentionItems} onViewDetails={setActiveSubTab} />

          <SubTabNav active={activeSubTab} onChange={setActiveSubTab} />

          {activeSubTab === 'overview' && (
            <OverviewPane
              selectedClient={selectedClient}
              performanceAnalytics={performanceAnalytics}
              onSessionSummary={setSessionFlagSummary}
              onViewDetails={setActiveSubTab}
            />
          )}
          {activeSubTab === 'strength' && (
            <StrengthPane selectedClient={selectedClient} performanceAnalytics={performanceAnalytics} />
          )}
          {activeSubTab === 'trainingLoad' && (
            <TrainingLoadPane
              performanceAnalytics={performanceAnalytics}
              activePieIndex={activePieIndex}
              setActivePieIndex={setActivePieIndex}
              MUSCLE_COLORS={MUSCLE_COLORS}
            />
          )}
          {activeSubTab === 'consistency' && (
            <ConsistencyPane
              selectedClient={selectedClient}
              performanceAnalytics={performanceAnalytics}
              clientMetrics={clientMetrics}
              sessionNoteEditing={sessionNoteEditing}
              setSessionNoteEditing={setSessionNoteEditing}
              sessionNoteText={sessionNoteText}
              setSessionNoteText={setSessionNoteText}
              sessionNoteSaving={sessionNoteSaving}
              saveSessionNote={saveSessionNote}
            />
          )}
        </>
      ) : (
        <div style={{ backgroundColor: bc.mediumGray, borderRadius: '14px', padding: '24px', textAlign: 'center', color: bc.silver }}>
          <span style={{ fontSize: '48px' }}>📊</span>
          <p>No workout data available yet</p>
        </div>
      )}
    </div>
  );
};

export default React.memo(ClientPerformanceTab, shallowEqualIgnoringFunctions);
